import { roundCurrency } from '@/lib/installments';

/**
 * Como o banco monta a série de parcelas. Ver ADR 0081.
 *
 * `FIXED` é o padrão e significa "o banco não disse / não sei" — é o que TODO contrato
 * cadastrado antes desta feature é. Não é Price: rotular a base instalada de Price seria
 * afirmar uma taxa que ninguém informou.
 *
 * `PRICE` não muda a forma da série (parcela Price é constante, que é o que o app já
 * fazia); o que ele acrescenta é conhecer a **Taxa do contrato**. `SAC` é a única curva
 * nova — parcela decrescente.
 */
export const AmortizationSystem = {
    FIXED: 'fixed',
    PRICE: 'price',
    SAC: 'sac',
} as const;

export type AmortizationSystemValue = typeof AmortizationSystem[keyof typeof AmortizationSystem];

export function isAmortizationSystem(value: unknown): value is AmortizationSystemValue {
    return value === AmortizationSystem.FIXED
        || value === AmortizationSystem.PRICE
        || value === AmortizationSystem.SAC;
}

/**
 * Os parâmetros da **Curva da parcela**, que vivem no contrato. É de propósito que isto
 * seja um subconjunto estrutural de `Financing`: uma linha de `financings` é passável
 * direto, sem adaptador.
 */
export interface FinancingCurve {
    amortizationSystem: string | null;
    /** Valor de contrato quando não há curva; também o histórico de antes da referência. */
    monthlyAmount: number;
    totalInstallments: number;
    /** Taxa do contrato, AO ANO e em pontos percentuais (8.99 = 8,99% a.a.). */
    annualRate: number | null;
    /** Saldo devedor que serve de base da derivação, válido ANTES de `curveInstallmentNumber`. */
    curveBalance: number | null;
    /** A primeira parcela derivada da curva. Antes dela o valor é histórico. */
    curveInstallmentNumber: number | null;
    /** Seguros e taxas: adicional fixo somado a toda parcela derivada. */
    curveExtraAmount: number | null;
}

/**
 * Taxa mensal EQUIVALENTE (capitalização composta), que é o que o SFH usa — e não
 * `i/12`. A diferença aparece em reais na parcela: 8,99% a.a. dá 0,72% a.m. por aqui e
 * 0,749% a.m. pela divisão simples.
 */
export function monthlyRateFromAnnual(annualPercent: number): number {
    if (!Number.isFinite(annualPercent) || annualPercent <= 0) return 0;
    return (1 + annualPercent / 100) ** (1 / 12) - 1;
}

/**
 * O contrato consegue derivar valor por número de parcela? Só SAC deriva: `fixed` e
 * `price` têm parcela constante, então o valor mensal já responde por todas.
 */
export function hasDerivedCurve(curve: FinancingCurve): boolean {
    return curve.amortizationSystem === AmortizationSystem.SAC
        && curve.annualRate != null
        && curve.curveBalance != null
        && curve.curveBalance > 0
        && curve.curveInstallmentNumber != null
        && curve.curveInstallmentNumber >= 1
        && getCurveInstallmentCount(curve) > 0;
}

/** Quantas parcelas a derivação cobre: da parcela de referência até o fim do contrato. */
function getCurveInstallmentCount(curve: FinancingCurve): number {
    if (curve.curveInstallmentNumber == null) return 0;
    return curve.totalInstallments - curve.curveInstallmentNumber + 1;
}

/**
 * A amortização mensal constante do SAC (saldo ÷ parcelas da derivação). É ela — e não o
 * valor da parcela — a régua certa para estimar quantas parcelas um abatimento mata no
 * modo "reduzir prazo", já que o corte vem do fim da série, onde a parcela é mais barata.
 */
export function getCurveAmortization(curve: FinancingCurve): number | null {
    if (!hasDerivedCurve(curve)) return null;
    return curve.curveBalance! / getCurveInstallmentCount(curve);
}

/**
 * O **Valor de contrato da parcela**: o que a parcela `installmentNumber` cobra pelo
 * contrato, antes de encargo e de pagamento parcial.
 *
 * É função pura do número da parcela — a curva NÃO é materializada em lugar nenhum, o que
 * preserva a invariante da ADR 0055 ("a base vem do contrato") e evita uma tabela de
 * cronograma que teria que ser mantida em sincronia na regeneração da série, na re-datação
 * por vencimento e nos três modos de abatimento.
 */
export function getContractInstallmentAmount(
    curve: FinancingCurve,
    installmentNumber: number,
): number {
    if (!hasDerivedCurve(curve)) return roundCurrency(curve.monthlyAmount);

    // Parcela anterior à referência é fato histórico: a derivação não tem o que dizer
    // sobre ela, porque o saldo informado vale a partir da referência.
    if (installmentNumber < curve.curveInstallmentNumber!) {
        return roundCurrency(curve.monthlyAmount);
    }

    const count = getCurveInstallmentCount(curve);
    const amortization = curve.curveBalance! / count;
    // Clamp: parcela além do prazo (série fora de sincronia com o total) devolve a última
    // da curva em vez de um valor negativo sem sentido.
    const index = Math.min(installmentNumber - curve.curveInstallmentNumber!, count - 1);
    const balanceBefore = curve.curveBalance! - amortization * index;
    const interest = balanceBefore * monthlyRateFromAnnual(curve.annualRate!);
    const extra = curve.curveExtraAmount ?? 0;

    return roundCurrency(Math.max(0, amortization + interest + extra));
}

/**
 * **Recalibração**: qual adicional fixo faz a curva bater com o boleto que realmente veio.
 *
 * Devolve o novo "seguros e taxas" (substitui o anterior, não acumula) — é o que absorve
 * TR, MIP/DFI e taxa de administração, que não estão na fórmula do SAC e fariam a
 * derivação pura sair sistematicamente abaixo do boleto.
 *
 * Pode ser negativo quando a taxa informada é alta demais; recusar isso faria a
 * recalibração falhar justamente quando ela é mais necessária.
 *
 * Só calcula — quem decide aplicar é o usuário: antecipação com desconto e encargo de
 * atraso chegam pelo mesmo caminho, e um mês atípico deslocaria o futuro inteiro.
 */
export function recalibrateExtraAmount(
    curve: FinancingCurve,
    installmentNumber: number,
    observedAmount: number,
): number | null {
    if (!hasDerivedCurve(curve)) return null;

    const withoutExtra = getContractInstallmentAmount(
        { ...curve, curveExtraAmount: 0 },
        installmentNumber,
    );
    return roundCurrency(observedAmount - withoutExtra);
}

/**
 * O contrato sabe abrir uma parcela em **principal** e **juros**?
 *
 * É pergunta DIFERENTE da que `hasDerivedCurve` responde. Aquela é sobre o *valor* da
 * parcela ("preciso derivar quanto essa parcela cobra?") e só o SAC precisa. Esta é sobre
 * a *composição* dele, e Price abre igualmente bem: a parcela é constante, mas com a taxa
 * conhecida dá para dizer quanto daquele valor virou juro do mês e quanto derrubou o
 * saldo. É literalmente o que a ADR 0081 §1 prometeu que a taxa acrescentaria.
 *
 * `fixed` nunca abre: sem taxa não existe conta que separe os dois, e chutar aqui seria
 * inventar o número que o usuário confere com o banco.
 */
export function canSplitInstallment(curve: FinancingCurve): boolean {
    const system = curve.amortizationSystem;
    if (system !== AmortizationSystem.SAC && system !== AmortizationSystem.PRICE) return false;
    return curve.annualRate != null
        && curve.annualRate > 0
        && curve.curveBalance != null
        && curve.curveBalance > 0
        && curve.curveInstallmentNumber != null
        && curve.curveInstallmentNumber >= 1
        && getCurveInstallmentCount(curve) > 0;
}

/** Uma parcela aberta: quanto dela abate a dívida, quanto é juro e quanto não é nem um nem outro. */
export interface InstallmentSplit {
    /** A parte que derruba o saldo devedor. */
    amortization: number;
    /** O juro do mês sobre o saldo em aberto. Sai do bolso e não abate nada. */
    interest: number;
    /** Seguros e taxas. Também não abate nada. */
    extra: number;
    /** Saldo devedor ANTES desta parcela. */
    balanceBefore: number;
    /** Saldo devedor DEPOIS que esta parcela é paga. */
    balanceAfter: number;
}

/**
 * O saldo devedor de HOJE (antes da próxima parcela pendente): a âncora informada pelo
 * usuário rolada para a frente pela amortização de cada parcela já paga. É o número que o
 * detalhe mostra como "Saldo devedor hoje" e o que a edição do contrato pré-preenche — os
 * dois saem daqui para não se contradizerem.
 *
 * `null` quando não dá para derivar: contrato `fixed` (sem taxa não há conta que separe
 * juro de principal) e saldo informado que se descolou da âncora. Abatimento "reduzir
 * prazo" e "abater da próxima" atualizam o saldo informado mas deixam a âncora da curva
 * intacta de propósito (lá a amortização mensal não muda, só somem parcelas do fim);
 * derivar a partir da âncora velha ignoraria o pagamento extra e mostraria uma dívida MAIOR
 * que a real. Enquanto o saldo não tiver âncora própria, o desencontro é o sinal de parar.
 */
export function getDerivedOutstandingBalance(
    financing: FinancingCurve & {
        lastKnownBalance?: number | null;
        nextPendingInstallmentNumber?: number | null;
    },
): number | null {
    const balanceMovedWithoutAnchor = financing.lastKnownBalance != null
        && financing.lastKnownBalance !== financing.curveBalance;
    if (balanceMovedWithoutAnchor) return null;
    // Sem pendente o contrato está quitado; o clamp da curva devolve zero.
    const nextNumber = financing.nextPendingInstallmentNumber ?? financing.totalInstallments + 1;
    return getBalanceBeforeInstallment(financing, nextNumber);
}

/**
 * O saldo devedor imediatamente ANTES da parcela `installmentNumber`, andando a partir da
 * referência informada pelo usuário.
 *
 * A âncora continua sendo o número do banco — o que a derivação faz é rolar esse número
 * para a frente pela conta do próprio contrato, uma parcela de cada vez. Não é o app
 * inventando saldo: é o app parando de mostrar um retrato vencido.
 *
 * Devolve `null` quando o contrato não sabe abrir a parcela, e também quando a conta sai
 * inconsistente (ver `stepAmortization`).
 */
export function getBalanceBeforeInstallment(
    curve: FinancingCurve,
    installmentNumber: number,
): number | null {
    if (!canSplitInstallment(curve)) return null;

    const anchor = curve.curveInstallmentNumber!;
    // Antes da referência a derivação não tem o que dizer: o saldo informado vale a partir
    // dela, e o que veio antes é fato histórico de outro nível da curva.
    if (installmentNumber <= anchor) return roundCurrency(curve.curveBalance!);

    const count = getCurveInstallmentCount(curve);
    const monthlyRate = monthlyRateFromAnnual(curve.annualRate!);

    // SAC tem amortização constante, então o saldo é forma fechada — e é exatamente a mesma
    // conta que `getContractInstallmentAmount` já faz para achar o juro da parcela. Manter
    // as duas idênticas é o que garante que parcela e saldo nunca se contradigam na tela.
    if (curve.amortizationSystem === AmortizationSystem.SAC) {
        const amortization = curve.curveBalance! / count;
        const steps = Math.min(installmentNumber - anchor, count);
        return roundCurrency(Math.max(0, curve.curveBalance! - amortization * steps));
    }

    // Price: a amortização cresce a cada mês, então não há atalho — anda parcela a parcela.
    // O laço é limitado pelo prazo do contrato (centenas, não milhares).
    let balance = curve.curveBalance!;
    const lastStep = Math.min(installmentNumber - 1, curve.totalInstallments);
    for (let k = anchor; k <= lastStep; k += 1) {
        const amortization = stepAmortization(curve, balance, monthlyRate);
        if (amortization == null) return null;
        balance = Math.max(0, balance - amortization);
        if (balance === 0) break;
    }
    return roundCurrency(balance);
}

/**
 * Quanto a parcela amortiza, dado o saldo em aberto.
 *
 * No Price devolve `null` quando a parcela não cobre nem o juro do mês: isso não é um
 * contrato exótico, é quase sempre taxa digitada errada (a.m. no campo que pede a.a.), e
 * derivar assim mesmo mostraria uma dívida CRESCENDO enquanto a pessoa paga — pior que o
 * saldo parado que estamos consertando. Melhor recusar e cair no valor informado.
 */
function stepAmortization(
    curve: FinancingCurve,
    balanceBefore: number,
    monthlyRate: number,
): number | null {
    if (curve.amortizationSystem === AmortizationSystem.SAC) {
        return curve.curveBalance! / getCurveInstallmentCount(curve);
    }
    // Seguros e taxas não amortizam: saem do valor do boleto antes da conta.
    const towardsContract = curve.monthlyAmount - (curve.curveExtraAmount ?? 0);
    const amortization = towardsContract - balanceBefore * monthlyRate;
    return amortization > 0 ? amortization : null;
}

/**
 * Abre a parcela `installmentNumber` em amortização, juros e seguros/taxas.
 *
 * É a resposta para "paguei R$ 2.000 de um SAC de R$ 220 mil — quanto disso saiu da
 * dívida?". Descontar os R$ 2.000 inteiros do saldo é o erro que esta função existe para
 * impedir: a maior parte da parcela inicial é juro do mês, e não abate nada.
 */
export function getInstallmentSplit(
    curve: FinancingCurve,
    installmentNumber: number,
): InstallmentSplit | null {
    const balanceBefore = getBalanceBeforeInstallment(curve, installmentNumber);
    if (balanceBefore == null) return null;

    const monthlyRate = monthlyRateFromAnnual(curve.annualRate!);
    const amortization = stepAmortization(curve, balanceBefore, monthlyRate);
    if (amortization == null) return null;

    // A última parcela não amortiza mais do que ainda se deve — arredondamento ao longo de
    // 300 meses deixaria um resto teimoso no fim da série.
    const applied = Math.min(amortization, balanceBefore);

    return {
        amortization: roundCurrency(applied),
        interest: roundCurrency(balanceBefore * monthlyRate),
        extra: roundCurrency(curve.curveExtraAmount ?? 0),
        balanceBefore: roundCurrency(balanceBefore),
        balanceAfter: roundCurrency(Math.max(0, balanceBefore - applied)),
    };
}
